// analytics/hooks/useInpatientsData.ts
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';  // Import useSelector instead of connect

import { inpatientsServices } from '../services/endpoints/inpatients';
import { IPersonalData, ReduxStore, IPatient, IDepartment } from '../../../../constants/types';
import { DistributionResponse } from '../types';
import { transformDischargeDiagnoses } from '../services/transformers';
import { isDischargeDiagnosesResponse } from '../utils/statsResponse';
import { updatePatientsFromId } from '../../../../redux/actions/patientsActions';


interface InpatientsData {
	readmissionRate: DistributionResponse;
	pharmacyUsage: DistributionResponse;
	admissionsByDepartment: DistributionResponse;
	indicatorsByDepartment: DistributionResponse;
}

export const inpatientsMethodMap = {
	readmissionRate: inpatientsServices.getReadmissionRate,
	pharmacyUsage: inpatientsServices.getPharmacyUsage,
	admissionsByDepartment: inpatientsServices.getAdmissionsByDepartment,
	indicatorsByDepartment: inpatientsServices.getIndicatorsByDepartment,
	dischargeDiagnoses: inpatientsServices.getDischargeDiagnoses,
};

interface UseInpatientsDataOptions {
	investigation: { uuid: string };
	departmentSelected?: string;
	startDate: string;
	endDate: string;
	requestedMethod: keyof typeof inpatientsMethodMap;
}

export const useInpatientsData = ({
	investigation,
	departmentSelected,
	startDate,
	endDate,
	requestedMethod,
}: UseInpatientsDataOptions) => {
	const [data, setData] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	// Get patients data from Redux store using useSelector
	const investigationsData = useSelector((state: ReduxStore) => {
		const investigations = state.hospital.data.departments;
		if (!investigations) return [];
		return investigations as IDepartment[];
	}, shallowEqual); // Add shallowEqual from react-redux

	const patientsData = useSelector((state: ReduxStore) => {
		const currentInvestigation = state.investigations.currentInvestigation;
		if (!currentInvestigation) return [];
		return state.patients.data?.[currentInvestigation.uuid] as IPatient[] || [];
	}, shallowEqual);

	const dispatch = useDispatch();
	const patientsFetchedRef = useRef(false);

	// Fetch patients once, outside the main data effect
	useEffect(() => {
		if (!patientsData.length && !patientsFetchedRef.current && investigation?.uuid) {
			patientsFetchedRef.current = true;
			dispatch(updatePatientsFromId(investigation.uuid, true));
		}
	}, [investigation?.uuid, patientsData.length, dispatch]);

	useEffect(() => {
		const abortController = new AbortController();

		const uuidDepartment = departmentSelected || 'all';

		if (!investigation?.uuid || !startDate || !endDate) {
			setLoading(false);
			return;
		}

		const fetchData = async () => {
			setLoading(true);
			setError(null);

			try {
				const fetchMethod = inpatientsMethodMap[requestedMethod];
				if (!fetchMethod) {
					throw new Error(`Unknown method: ${requestedMethod}`);
				}

				const response = await fetchMethod(
					investigation.uuid,
					uuidDepartment,
					startDate,
					endDate,
					abortController.signal
				);

				setData(response);
			} catch (err) {
				console.error('Error fetching inpatients data:', err);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
		return () => abortController.abort();
	}, [investigation?.uuid, departmentSelected, startDate, endDate, requestedMethod]);

	// SHER-583. The discharge table renders the same rows as the outpatient one, Female/Male
	// included. Patient sex is encrypted, so the split can only be counted in the browser — and it
	// is counted where the rows are assembled, in the transformer beside its outpatient twin,
	// rather than in the chart layer. Doing it there meant every chart card on every tab
	// subscribing to the patient store and building its own id-to-sex map to serve one table.
	const viewData = useMemo(() => {
		// `data` passes through untouched unless it really is a discharge-diagnoses response. A
		// failed fetch leaves it null and ChartFactory's `!originalData` guard renders a
		// placeholder; returning `{ rows: [] }` there would make it truthy, skip that guard, and
		// show "no ICD-11 field configured on your discharge form" after a network error — the two
		// different answers collapsed into one sentence that this table was built to avoid. An
		// EMPTY success is different and must stay truthy, which `{ rows: [] }` is.
		if (requestedMethod !== 'dischargeDiagnoses' || !isDischargeDiagnosesResponse(data)) {
			return data;
		}

		return transformDischargeDiagnoses(data, patientsData);
	}, [data, requestedMethod, patientsData]);

	return { data: viewData, loading, error };
};
